import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Group, Stack, Text, UnstyledButton} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {getOrCreateDraftState} from 'llm/chat-library/ChatDraftState';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {NewChatInput} from '../ChatInput';
import type {NewChatInputHandle} from '../ChatInput';
import type {CreateChatResult} from './ChatRouter';
import {getDefaultAgentType} from './ChatTypeUtil';
import {
  ONBOARDING_CHIP_STAGGER_MS,
  ONBOARDING_COMPLETED_HEADING_DELAY_MS,
  ONBOARDING_COMPLETED_PANELS_BASE_DELAY_MS,
  OnboardingStaggeredEnterFade,
} from './OnboardingComponents';
import {OnboardingHeading} from './OnboardingHeading';
import {OnboardingSuccessIcon} from './OnboardingSuccessIcon';

const TEST_COMPONENT = 'AIOnboardingCompletedScreen';
const COMPLETED_ENTRANCE_KEY = 'onboarding-completed';
const DEFAULT_TITLE = "You're all set";
const DEFAULT_SUBTITLE = 'Benchling AI is now tuned to your work. Ask anything below.';
/** Prompt-card width when the sidepanel is semi-expanded (448px content area). */
const CARD_WIDTH_SEMI_EXPANDED_PX = 122;
/** Prompt-card width when the sidepanel is fully expanded. */
const CARD_WIDTH_EXPANDED_PX = 132;

/** A single completion-page prompt card. */
export interface OnboardingCompletedCard {
  label: string;
  prompt: string;
  /**
   * Per-card icon, overriding the screen-level {@link AIOnboardingCompletedScreenProps.icon}. Used by
   * the Gemini path, where each suggestion can come from a different progressive-disclosure category and
   * should show that category's icon.
   */
  icon?: BenchlingIconName;
  /** Fired (e.g. for analytics) when the card is chosen, before the chat is submitted. */
  onSelect: () => void;
}

interface AIOnboardingCompletedScreenProps {
  /** Default card icon, used for cards that don't specify their own (e.g. the selected category's icon). */
  icon?: BenchlingIconName;
  /** The prompt options presented as cards. Omit/empty to show the completion page with no prompts. */
  cards?: ReadonlyArray<OnboardingCompletedCard>;
  /** Where these cards came from; recorded on the completion analytics event. */
  path?: 'category' | 'gemini';
  /** Called with the created chat once the first chat has been submitted. */
  onSubmitFirstChat: (result: CreateChatResult) => void;
  /** Called when no agent type is available, so submission can't happen (falls back to a skip). */
  onNoAgentAvailable: () => void;
  title?: string;
  subtitle?: string;
  /** Semi-expanded (448px) uses narrower prompt cards; expanded uses the full card width. */
  isExpanded?: boolean;
}

/**
 * The "You're all set" onboarding completion page. Renders a celebratory hero, the chosen prompt
 * options as cards (if any), and a chat input pinned to the bottom.
 *
 * The cards reuse the progressive-disclosure interaction: hovering a card pre-fills the chat input with
 * its prompt, and clicking it submits that prompt as the user's first chat (via {@link NewChatInput}'s
 * imperative handle, mirroring `WelcomeToChatPanel`). The user can also ignore the cards and type their
 * own first message. Each card shows its own {@link OnboardingCompletedCard.icon} when set, otherwise
 * the screen-level {@link icon}. With no cards (e.g. when Gemini can't mint suggestions) the page still
 * shows the hero and input so the user can type their first message.
 *
 * Renders inside the onboarding shell provided by the host, so it adds no chrome of its own.
 */
export function AIOnboardingCompletedScreen({
  icon,
  cards = [],
  path,
  onSubmitFirstChat,
  onNoAgentAvailable,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  isExpanded = true,
}: AIOnboardingCompletedScreenProps): JSX.Element | null {
  const {classes} = useStyles({isExpanded});
  const newChatInputRef = useRef<NewChatInputHandle>(null);
  const [isPreviewText, setIsPreviewText] = useState(false);
  // The submission origin read by handleCreateChat: card clicks set it to `path`; typed messages keep
  // the default 'typed'. onCreateChat carries no per-submit path, so we stash it here.
  const submissionPathRef = useRef<'category' | 'gemini' | 'typed'>('typed');
  // Guards against a second card click creating a second chat: submitWithText does not consult the
  // input's own isSubmitting state.
  const isSubmittingRef = useRef(false);

  // The bottom input relies on a persisted draft (with an agent type) to submit. During onboarding no
  // route has called openDraftChat, so seed one here. With no agent available there's nothing to submit
  // to, so fall back to a skip rather than stranding the user on dead cards.
  const [hasAgent] = useState(() => getDefaultAgentType() != null);
  useOnMount(() => {
    if (!hasAgent) {
      onNoAgentAvailable();
      return;
    }
    getOrCreateDraftState(null);
  });

  const handleHoverCard = useCallback((prompt: string | null) => {
    if (prompt != null) {
      newChatInputRef.current?.setPreviewText(prompt);
      setIsPreviewText(true);
    } else {
      newChatInputRef.current?.clearPreviewText();
      setIsPreviewText(false);
    }
  }, []);

  const handleSelectCard = useCallback(
    (card: OnboardingCompletedCard) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      submissionPathRef.current = path ?? 'typed';
      card.onSelect();
      setIsPreviewText(false);
      // submitWithText handles its own errors; nothing to await here.
      newChatInputRef.current?.submitWithText(card.prompt).catch(() => {});
    },
    [path]
  );

  const handleCreateChat = useCallback(
    (result: CreateChatResult) => {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.COMPLETED_ONBOARDING_FIRST_CHAT, {
        path: submissionPathRef.current,
        chatSessionId: result.chatSessionId,
      });
      onSubmitFirstChat(result);
    },
    [onSubmitFirstChat]
  );

  if (!hasAgent) {
    return null;
  }

  return (
    <Box className={classes.root} {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
      <Stack className={classes.topZone} align="center" spacing="xl">
        <Stack align="center" spacing="sm">
          <OnboardingStaggeredEnterFade animationKey={COMPLETED_ENTRANCE_KEY} variant="fade">
            <Box className={classes.successIconCenter}>
              <OnboardingSuccessIcon />
            </Box>
          </OnboardingStaggeredEnterFade>
          <OnboardingStaggeredEnterFade
            animationKey={COMPLETED_ENTRANCE_KEY}
            delayMs={ONBOARDING_COMPLETED_HEADING_DELAY_MS}
            variant="fade"
          >
            <OnboardingHeading title={title} subtitle={subtitle} variant="survey" />
          </OnboardingStaggeredEnterFade>
        </Stack>
        {cards.length > 0 && (
          <Group spacing="sm" position="center" className={classes.cards} align="stretch">
            {cards.map((card, index) => {
              const cardIcon = card.icon ?? icon;
              return (
                <OnboardingStaggeredEnterFade
                  key={card.label}
                  animationKey={COMPLETED_ENTRANCE_KEY}
                  delayMs={ONBOARDING_COMPLETED_PANELS_BASE_DELAY_MS + index * ONBOARDING_CHIP_STAGGER_MS}
                  variant="rise"
                >
                  <UnstyledButton
                    className={classes.card}
                    onMouseEnter={() => {
                      handleHoverCard(card.prompt);
                    }}
                    onMouseLeave={() => {
                      handleHoverCard(null);
                    }}
                    onFocus={() => {
                      handleHoverCard(card.prompt);
                    }}
                    onBlur={() => {
                      handleHoverCard(null);
                    }}
                    onClick={() => {
                      handleSelectCard(card);
                    }}
                    {...getTestProps({component: TEST_COMPONENT, element: 'card'})}
                  >
                    {cardIcon != null && (
                      <BenchlingIcon icon={cardIcon} aria-hidden={true} className={classes.cardIcon} />
                    )}
                    <Text size="lg" fw={500} lh={1.3} className={classes.cardLabel}>
                      {card.label}
                    </Text>
                  </UnstyledButton>
                </OnboardingStaggeredEnterFade>
              );
            })}
          </Group>
        )}
      </Stack>
      <Box className={classes.inputZone}>
        {/* Draft state (including agentType) is seeded on mount above. */}
        <NewChatInput ref={newChatInputRef} onCreateChat={handleCreateChat} isPreviewText={isPreviewText} />
      </Box>
    </Box>
  );
}

const useStyles = createStyles((theme, {isExpanded}: {isExpanded: boolean}) => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Hero + cards, centered in the space above the bottom-pinned input.
  topZone: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    justifyContent: 'center',
    WebkitFontSmoothing: 'antialiased',
  },
  successIconCenter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  cards: {
    flexWrap: 'nowrap',
    alignSelf: 'stretch',
  },
  card: {
    flex: '0 0 auto',
    width: isExpanded ? CARD_WIDTH_EXPANDED_PX : CARD_WIDTH_SEMI_EXPANDED_PX,
    maxWidth: isExpanded ? CARD_WIDTH_EXPANDED_PX : CARD_WIDTH_SEMI_EXPANDED_PX,
    minHeight: 144,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: theme.spacing.lg,
    borderRadius: theme.radius.lg,
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[8],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  cardIcon: {
    flex: 'none',
    width: 18,
    height: 18,
    fontSize: 18,
    color: theme.colors.blue[7],
  },
  cardLabel: {
    flex: 'none',
    alignSelf: 'stretch',
    marginTop: `calc(${theme.spacing['3xl']} + ${theme.spacing.sm})`,
    paddingTop: theme.spacing.sm,
    borderTop: `1px solid ${theme.colors.blue[2]}`,
    color: theme.colors.blue[7],
  },
  inputZone: {
    flex: 'none',
  },
}));
